import useLocalStorage from "@dashboard/hooks/useLocalStorage";
import { Box, Button, Drawer, Text, Tooltip } from "@saleor/macaw-ui-next";
import { Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { type ReactElement } from "react";
import { useIntl } from "react-intl";

import { SidebarContent } from "./Content";
import { CollapsedMenu } from "./menu/CollapsedMenu";
import { sidebarMessages } from "./messages";
import classes from "./Sidebar.module.css";
import { useSidebarBreakpointContext } from "./SidebarContext";
import { UserControls } from "./user/Controls";

const SIDEBAR_WIDTH = "260px";
const COLLAPSED_SIDEBAR_WIDTH = "64px";
const SIDEBAR_COLLAPSED_STORAGE_KEY = "sidebarCollapsed";

interface CollapseButtonProps {
  isCollapsed: boolean;
  onClick: () => void;
}

const CollapseButton = ({ isCollapsed, onClick }: CollapseButtonProps): ReactElement => {
  const intl = useIntl();
  const message = isCollapsed ? sidebarMessages.expand : sidebarMessages.collapse;
  const label = intl.formatMessage(message);

  return (
    <Tooltip>
      <Tooltip.Trigger>
        <Button
          variant="tertiary"
          size="medium"
          icon={
            isCollapsed ? (
              <PanelLeftOpen size={16} color="var(--mu-colors-text-default2)" />
            ) : (
              <PanelLeftClose size={16} color="var(--mu-colors-text-default2)" />
            )
          }
          aria-label={label}
          aria-expanded={!isCollapsed}
          data-test-id="sidebar-collapse-button"
          onClick={onClick}
        />
      </Tooltip.Trigger>
      <Tooltip.Content side={isCollapsed ? "right" : "bottom"}>
        <Tooltip.Arrow />
        <Text size={2}>{label}</Text>
      </Tooltip.Content>
    </Tooltip>
  );
};

const DesktopSidebar = (): ReactElement => {
  const [isCollapsed, setIsCollapsed] = useLocalStorage(SIDEBAR_COLLAPSED_STORAGE_KEY, false);
  const toggle = (): void => setIsCollapsed(collapsed => !collapsed);

  return (
    <Box
      __width={isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_WIDTH}
      height="100%"
      className={classes.desktopSidebar}
      data-test-id="desktop-sidebar"
    >
      {isCollapsed ? (
        <Box
          as="aside"
          height="100%"
          display="grid"
          __gridTemplateRows="auto 1fr auto"
          backgroundColor="default2"
        >
          <Box display="flex" justifyContent="center" paddingY={4}>
            <CollapseButton isCollapsed onClick={toggle} />
          </Box>
          <CollapsedMenu />
          <Box
            display="flex"
            justifyContent="center"
            paddingY={4}
            borderTopWidth={1}
            borderTopStyle="solid"
            borderColor="default1"
          >
            <UserControls />
          </Box>
        </Box>
      ) : (
        <SidebarContent headerAction={<CollapseButton isCollapsed={false} onClick={toggle} />} />
      )}
    </Box>
  );
};

export const Sidebar = (): ReactElement => {
  const { breakpoint } = useSidebarBreakpointContext();

  if (breakpoint === "wide") {
    return (
      <>
        <div className={classes.fullSidebarWide}>
          <DesktopSidebar />
        </div>
        <div className={classes.drawerWide}>
          <Drawer>
            <Drawer.Trigger>
              <Box
                as="button"
                borderWidth={0}
                backgroundColor="default1"
                cursor="pointer"
                data-test-id="sidebar-drawer-open"
              >
                <Menu />
              </Box>
            </Drawer.Trigger>
            <Drawer.Content
              backgroundColor="default2"
              data-test-id="sidebar-drawer-content"
              paddingTop={0}
              __width={SIDEBAR_WIDTH}
            >
              <SidebarContent />
            </Drawer.Content>
          </Drawer>
        </div>
      </>
    );
  }

  return (
    <>
      <Box display={{ mobile: "none", tablet: "none", desktop: "block" }} height="100%">
        <DesktopSidebar />
      </Box>
      <Box display={{ mobile: "block", tablet: "block", desktop: "none" }}>
        <Drawer>
          <Drawer.Trigger>
            <Box
              as="button"
              borderWidth={0}
              backgroundColor="default1"
              cursor="pointer"
              data-test-id="sidebar-drawer-open"
            >
              <Menu />
            </Box>
          </Drawer.Trigger>
          <Drawer.Content
            backgroundColor="default2"
            data-test-id="sidebar-drawer-content"
            paddingTop={0}
            __width={SIDEBAR_WIDTH}
          >
            <SidebarContent />
          </Drawer.Content>
        </Drawer>
      </Box>
    </>
  );
};
